// ./src/utils/api/EventTravelSegmentService.ts
//
// Event travel segment service — one leg of a journey to or from an event
// (flight, train, shuttle), who is on it, and who is collecting them.
// OpResult-native, event-scoped: every list is filtered by eventId. Full
// single-item CRUD plus a bulk status / direction / pickup / responsible change
// or remove (array of ids in the where clause). See em-scaffold-ui.
//
// TIMEZONES: departsAt / arrivesAt are UTC instants, each PAIRED with its own end's
// IANA zone (fromTimezone / toTimezone). Never send or store a UTC offset — an
// offset is only correct today and is an hour wrong across any DST boundary.
// Duration is derived, never entered; the server rejects an arrival at or before
// its departure because that is the signature of a local time sent in the wrong
// zone. See docs/event-travel-architecture.md.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Enum-like constants (mirror the eventsmagic-api boundary/travel.ts)
// =============================================================================

export const TRAVEL_DIRECTION = {
  ARRIVAL: "arrival",
  DEPARTURE: "departure",
  TRANSFER: "transfer",
  OTHER: "other",
} as const;

export const TRAVEL_DIRECTION_VALUES = Object.values(TRAVEL_DIRECTION);

export const TRAVEL_MODE = {
  FLIGHT: "flight",
  TRAIN: "train",
  BUS: "bus",
  SHUTTLE: "shuttle",
  CAR: "car",
  FERRY: "ferry",
  RIDESHARE: "rideshare",
  OTHER: "other",
} as const;

export const TRAVEL_MODE_VALUES = Object.values(TRAVEL_MODE);

export const TRAVEL_STATUS = {
  PLANNED: "planned",
  BOOKED: "booked",
  CONFIRMED: "confirmed",
  DELAYED: "delayed",
  CANCELLED: "cancelled",
  COMPLETED: "completed",
} as const;

export const TRAVEL_STATUS_VALUES = Object.values(TRAVEL_STATUS);

/** No longer worth chasing — the board dims these and a future poller skips them. */
export const TRAVEL_STATUS_TERMINAL: string[] = [TRAVEL_STATUS.CANCELLED, TRAVEL_STATUS.COMPLETED];

export const TRAVEL_PARTY_KIND = {
  USER: "user",
  CREW: "crew",
  GUEST: "guest",
  VENDOR: "vendor",
  VENUE: "venue",
} as const;

export const TRAVEL_PARTY_KIND_VALUES = Object.values(TRAVEL_PARTY_KIND);


// =============================================================================
// GraphQL
// =============================================================================

const TRAVELER_FIELDS = `
  id
  travelSegmentId
  eventGuestId
  travelerLabel
  seat
  ticketRef
  orderNo
  status
`;

const TRAVEL_SEGMENT_FIELDS = `
  id
  eventId
  accountId
  direction
  travelMode
  carrier
  serviceNumber
  bookingRef
  fromHubId
  fromVenueId
  fromLabel
  fromCode
  fromTimezone
  fromDetail
  toHubId
  toVenueId
  toLabel
  toCode
  toTimezone
  toDetail
  departsAt
  arrivesAt
  travelStatus
  needsPickup
  responsibleKind
  responsibleId
  responsibleLabel
  notes
  enteredByKind
  orderNo
  status
  rights
  createdAt
  updatedAt
  travelers { ${TRAVELER_FIELDS} }
`;

const TRAVEL_SEGMENT_LIST_QUERY = gql`
  query EventTravelSegmentList($filter: EventTravelSegmentFilter, $params: PaginationAndSorting) {
    eventTravelSegmentList(filter: $filter, params: $params) {
      code
      data { ${TRAVEL_SEGMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TRAVEL_SEGMENT_GET_QUERY = gql`
  query EventTravelSegmentGet($id: ID) {
    eventTravelSegmentGet(id: $id) {
      code
      data { ${TRAVEL_SEGMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TRAVEL_SEGMENT_GET_MANY_QUERY = gql`
  query EventTravelSegmentGetMany($ids: [ID]) {
    eventTravelSegmentGetMany(ids: $ids) {
      code
      data { ${TRAVEL_SEGMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TRAVEL_SEGMENT_CREATE_MUTATION = gql`
  mutation EventTravelSegmentCreate($params: EventTravelSegmentInput) {
    eventTravelSegmentCreate(params: $params) {
      code
      data { ${TRAVEL_SEGMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TRAVEL_SEGMENT_UPDATE_MUTATION = gql`
  mutation EventTravelSegmentUpdate($where: EventTravelSegmentWhereInput, $params: EventTravelSegmentInput) {
    eventTravelSegmentUpdate(where: $where, params: $params) {
      code
      data { ${TRAVEL_SEGMENT_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const TRAVEL_SEGMENT_REMOVE_MUTATION = gql`
  mutation EventTravelSegmentRemove($where: EventTravelSegmentWhereInput) {
    eventTravelSegmentRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api EventTravelSegment schema)
// =============================================================================

export interface TravelTraveler {
  id?: string;
  travelSegmentId?: string | null;
  /** Someone on the event's guest list (guests AND crew). Null for an off-list plus-one. */
  eventGuestId?: string | null;
  /** Name snapshot — survives the guest row being removed. */
  travelerLabel?: string | null;
  /** Per traveller: four people on one flight have four different seats. */
  seat?: string | null;
  ticketRef?: string | null;
  orderNo?: number | null;
  status?: number;
}

export interface TravelSegment {
  id?: string;
  eventId?: string | null;
  accountId?: string | null;
  direction?: string | null;
  travelMode?: string | null;
  carrier?: string | null;
  serviceNumber?: string | null;
  bookingRef?: string | null;
  fromHubId?: string | null;
  /** Soft ref → Venue when the leg starts somewhere already on file. */
  fromVenueId?: string | null;
  fromLabel?: string | null;
  fromCode?: string | null;
  /** IANA zone of the ORIGIN — departsAt is UTC and renders in this zone. */
  fromTimezone?: string | null;
  fromDetail?: string | null;
  toHubId?: string | null;
  toVenueId?: string | null;
  toLabel?: string | null;
  toCode?: string | null;
  /** IANA zone of the DESTINATION — arrivesAt is UTC and renders in this zone. */
  toTimezone?: string | null;
  toDetail?: string | null;
  departsAt?: string | null;
  arrivesAt?: string | null;
  travelStatus?: string | null;
  needsPickup?: boolean | null;
  responsibleKind?: string | null;
  responsibleId?: string | null;
  responsibleLabel?: string | null;
  notes?: string | null;
  /** Provenance: user | crew | guest | visitor. 'visitor' = self-reported by a guest. */
  enteredByKind?: string | null;
  orderNo?: number | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
  travelers?: TravelTraveler[] | null;
}

export interface TravelTravelerInput {
  id?: string | null;
  eventGuestId?: string | null;
  travelerLabel?: string | null;
  seat?: string | null;
  ticketRef?: string | null;
  orderNo?: number | null;
}

export interface TravelSegmentInput {
  eventId?: string | null;
  direction?: string | null;
  travelMode?: string | null;
  carrier?: string | null;
  serviceNumber?: string | null;
  bookingRef?: string | null;
  fromHubId?: string | null;
  /** Soft ref → Venue when the leg starts somewhere already on file. */
  fromVenueId?: string | null;
  fromLabel?: string | null;
  fromCode?: string | null;
  fromTimezone?: string | null;
  fromDetail?: string | null;
  toHubId?: string | null;
  toVenueId?: string | null;
  toLabel?: string | null;
  toCode?: string | null;
  toTimezone?: string | null;
  toDetail?: string | null;
  departsAt?: string | null;
  arrivesAt?: string | null;
  travelStatus?: string | null;
  needsPickup?: boolean | null;
  responsibleKind?: string | null;
  responsibleId?: string | null;
  notes?: string | null;
  orderNo?: number | null;
  status?: number | null;
  /** Omit to leave travellers untouched; send [] to clear them. */
  travelers?: TravelTravelerInput[] | null;
}

// A bulk update (>1 id) may only mass-set what genuinely applies to a whole
// selection — times and per-leg identifiers stay single-item.
export interface TravelSegmentBulkInput {
  direction?: string | null;
  travelStatus?: string | null;
  needsPickup?: boolean | null;
  responsibleKind?: string | null;
  responsibleId?: string | null;
}

export interface TravelSegmentFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  direction?: string[] | null;
  travelMode?: string[] | null;
  travelStatus?: string[] | null;
  needsPickup?: boolean | null;
  responsibleKind?: string[] | null;
  responsibleId?: string[] | null;
  status?: number[] | null;
  searchKeyword?: string | null;
}

export interface TravelSegmentWhereInput {
  id?: string | string[];
}

// =============================================================================
// Service
// =============================================================================

// ── The travel report (#48) ──────────────────────────────────────────────────
// ONE server aggregate behind the whole page. ⚠ Every local day and time is computed SERVER-side
// and sent as a string. The browser must never re-derive one: it would do it in the VIEWER's
// timezone and quietly disagree with the sheet printed beside it.
const TRAVEL_REPORT_QUERY = gql`
  query EventTravelReport($params: TravelReportStatsInput) {
    eventTravelReport(params: $params) {
      code
      data {
        eventId
        includeCancelled
        participation
        direction
        segmentCount
        arrivalCount
        departureCount
        travelerCount
        cancelledCount
        unplaceableCount
        pickupCount
        unassignedPickupCount
        incompleteItineraryCount
        busiestDay
        days { day segments travelers pickups }
        modes { mode segments travelers }
        hubs { key code label arrivals departures travelers modes { mode segments travelers } }
        segments {
          travelSegmentId direction travelMode travelStatus isCancelled
          carrier serviceNumber bookingRef
          fromLabel fromCode fromDetail toLabel toCode toDetail
          departsLocal { day time timeZone }
          arrivesLocal { day time timeZone }
          localDay localTime localTimeZone isPlaceable isLive isRun hubKey
          needsPickup responsibleKind responsibleLabel pickupUnassigned
          notes travelerCount
          travelers { travelerId eventGuestId name email phone rsvpStatus seat ticketRef isOffList }
        }
        pickups {
          travelSegmentId direction travelMode travelStatus carrier serviceNumber
          fromLabel fromCode toLabel toCode toDetail
          localDay localTime localTimeZone isPlaceable
          needsPickup responsibleLabel pickupUnassigned notes travelerCount
          travelers { travelerId eventGuestId name email phone seat isOffList }
        }
        travelers {
          eventGuestId name email phone rsvpStatus isOffList
          legCount hasArrival hasDeparture isComplete missingLeg
          legs {
            travelSegmentId direction travelMode travelStatus carrier serviceNumber
            fromCode fromLabel toCode toLabel
            localDay localTime localTimeZone isPlaceable isCancelled needsPickup seat ticketRef
          }
        }
      }
      errors { name errors warnings }
    }
  }
`;

/**
 * Which travellers the by-traveller view lists — mirrors TRAVEL_REPORT_PARTICIPATION.
 * ⚠ `incomplete` is the chase list, and it is NOT "guests with no travel": most guests are local
 * and never fly. It means somebody flying in with no way home on file.
 */
export const TRAVEL_REPORT_PARTICIPATION = {
  ALL: "all",
  COMPLETE: "complete",
  INCOMPLETE: "incomplete",
} as const;

/** The directions that count as an airport run. ⚠ A transfer is shown but is never a run. */
export const TRAVEL_REPORT_RUN_DIRECTION = {
  ARRIVAL: "arrival",
  DEPARTURE: "departure",
} as const;

/** A UTC instant already resolved onto one end of the journey's own clock, by the SERVER. */
export interface TravelLocalMoment {
  day?: string | null;
  time?: string | null;
  timeZone?: string | null;
}

export interface TravelReportTraveler {
  travelerId?: string | null;
  /** Null for an off-list plus-one — on the plane, not on the guest list. */
  eventGuestId?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  rsvpStatus?: string | null;
  seat?: string | null;
  ticketRef?: string | null;
  isOffList?: boolean | null;
}

export interface TravelReportSegment {
  travelSegmentId?: string | null;
  direction?: string | null;
  travelMode?: string | null;
  travelStatus?: string | null;
  isCancelled?: boolean | null;
  carrier?: string | null;
  serviceNumber?: string | null;
  bookingRef?: string | null;
  fromLabel?: string | null;
  fromCode?: string | null;
  fromDetail?: string | null;
  toLabel?: string | null;
  toCode?: string | null;
  toDetail?: string | null;
  departsLocal?: TravelLocalMoment | null;
  arrivesLocal?: TravelLocalMoment | null;
  /** ⚠ The day this journey is planned on, in the RIGHT zone. Never recompute it here. */
  localDay?: string | null;
  localTime?: string | null;
  localTimeZone?: string | null;
  /** False when there is no usable time or zone — it cannot go on a calendar at all. */
  isPlaceable?: boolean | null;
  isLive?: boolean | null;
  isRun?: boolean | null;
  /** Which hub's bar this journey sits under. Null for a transfer, which belongs to no hub. */
  hubKey?: string | null;
  needsPickup?: boolean | null;
  responsibleKind?: string | null;
  responsibleLabel?: string | null;
  pickupUnassigned?: boolean | null;
  notes?: string | null;
  travelerCount?: number | null;
  travelers?: TravelReportTraveler[] | null;
}

export interface TravelReportDay {
  day?: string | null;
  segments?: number | null;
  travelers?: number | null;
  pickups?: number | null;
}

/** How many journeys of ONE transport type, and how many people on them. */
export interface TravelReportMode {
  mode?: string | null;
  segments?: number | null;
  travelers?: number | null;
}

export interface TravelReportHub {
  /** ⚠ The SERVER's grouping key. Match a segment's `hubKey` against it — never re-derive it. */
  key?: string | null;
  code?: string | null;
  label?: string | null;
  arrivals?: number | null;
  departures?: number | null;
  travelers?: number | null;
  /** How people get here — a hub reached by shuttle is a different job from one reached by air. */
  modes?: TravelReportMode[] | null;
}

export interface TravelReportLeg {
  travelSegmentId?: string | null;
  direction?: string | null;
  travelMode?: string | null;
  travelStatus?: string | null;
  carrier?: string | null;
  serviceNumber?: string | null;
  fromCode?: string | null;
  fromLabel?: string | null;
  toCode?: string | null;
  toLabel?: string | null;
  localDay?: string | null;
  localTime?: string | null;
  localTimeZone?: string | null;
  isPlaceable?: boolean | null;
  isCancelled?: boolean | null;
  needsPickup?: boolean | null;
  seat?: string | null;
  ticketRef?: string | null;
}

export interface TravelReportTravelerItinerary {
  eventGuestId?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  rsvpStatus?: string | null;
  isOffList?: boolean | null;
  legCount?: number | null;
  hasArrival?: boolean | null;
  hasDeparture?: boolean | null;
  isComplete?: boolean | null;
  /** arrival | departure — the end that is missing. Null when complete. */
  missingLeg?: string | null;
  legs?: TravelReportLeg[] | null;
}

export interface TravelReportStats {
  eventId?: string | null;
  includeCancelled?: boolean | null;
  participation?: string | null;
  direction?: string | null;
  segmentCount?: number | null;
  arrivalCount?: number | null;
  departureCount?: number | null;
  travelerCount?: number | null;
  cancelledCount?: number | null;
  unplaceableCount?: number | null;
  pickupCount?: number | null;
  unassignedPickupCount?: number | null;
  incompleteItineraryCount?: number | null;
  busiestDay?: string | null;
  days?: TravelReportDay[] | null;
  hubs?: TravelReportHub[] | null;
  /** Transport types across the whole event — an airport job or a coach job? */
  modes?: TravelReportMode[] | null;
  segments?: TravelReportSegment[] | null;
  pickups?: TravelReportSegment[] | null;
  travelers?: TravelReportTravelerItinerary[] | null;
}

export class EventTravelSegmentService {
  /**
   * The travel report (#48). ⚠ There is no `audience` option, and its absence is deliberate: a
   * plane lands regardless of anyone's RSVP status, and filtering airport runs by it is how
   * somebody gets left at an arrivals gate.
   */
  static async report(
    eventId: string,
    options?: { includeCancelled?: boolean; participation?: string; direction?: string | null },
  ): Promise<OpResult> {
    return ApiService.executeQuery(TRAVEL_REPORT_QUERY, {
      params: {
        eventId,
        includeCancelled: options?.includeCancelled ?? true,
        participation: options?.participation ?? TRAVEL_REPORT_PARTICIPATION.ALL,
        direction: options?.direction ?? undefined,
      },
    });
  }

  static async list(
    filter?: TravelSegmentFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(TRAVEL_SEGMENT_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(TRAVEL_SEGMENT_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeQuery(TRAVEL_SEGMENT_GET_MANY_QUERY, { ids });
  }

  static async create(params: TravelSegmentInput): Promise<OpResult> {
    return ApiService.executeMutation(TRAVEL_SEGMENT_CREATE_MUTATION, { params });
  }

  /**
   * Every leg for an event — for CSV export and the day-grouped board, which must not show a partial itinerary.
   * ⚠ ROADMAP #118: this was ONE page of 500 under the name `listAll` ("an event's travel is tens of rows"); a
   * festival's crew travel is not, and the board would have ended mid-day without a word. It pages now.
   */
  static async listAll(filter?: TravelSegmentFilter, sortBy?: SortItem[]): Promise<OpResult> {
    return listAllPages<TravelSegment>(EventTravelSegmentService.list, filter, sortBy);
  }

  static async update(where: TravelSegmentWhereInput, params: TravelSegmentInput): Promise<OpResult> {
    return ApiService.executeMutation(TRAVEL_SEGMENT_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(TRAVEL_SEGMENT_REMOVE_MUTATION, { where: { id } });
  }

  /** Mass-set direction / status / pickup / responsible on every selected leg in one call. */
  static async bulkUpdate(ids: string[], params: TravelSegmentBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(TRAVEL_SEGMENT_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected leg in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(TRAVEL_SEGMENT_REMOVE_MUTATION, { where: { id: ids } });
  }
}
